import { descriptorPropTypes } from "lib/common/descriptorcontrols/formhelpers";
import EntityProxy from "data/util/EntityProxy";
import entityMetaStore from "data/meta/entityMetaStore";
import PropTypes from "prop-types";
import RecordGridDataGateway, {
    buildColumnsFromConfig,
    buildCreatable,
    buildDevicelesiongridCheckboxColumns,
} from "../RecordGridDataGateway";
import React from "react";
import RecordActions, { OPT_USER_CONFIRMED } from "data/RecordActions";
import Dispatcher from "Dispatcher";
import Control
    from "lib/common/descriptorcontrols/AssociationField/devicelesiongrid";

/**
 * DeviceLesionGrid association field component with enablement tracking.
 * 
 * This component tracks enablement state changes to handle the scenario where
 * a parent field's EnableScript dynamically disables the grid. Since React's
 * shallow comparison doesn't detect changes within the same EntityProxy reference,
 * we explicitly track the enabled state and clear grid items when transitioning
 * from enabled to disabled.
 * 
 * **Key Difference from CompositionField:**
 * AssociationField base records (devices, workshops) are owned by the parentRecord's
 * composition. clearGridItems performs a two-phase delete: first removes each join
 * record from activeRecord, then deletes the base record from parentRecord if no
 * other sibling column entity still references it.
 * 
 * @see HJC-661 - Correct issue with devicelesiongrid enablement
 */
export class AssociationFielddevicelesiongrid extends React.Component {
    /**
     * Tracks enablement state to detect when the field's EnableScript result changes.
     * Since activeRecord is the same EntityProxy instance across renders, React's
     * shallow comparison won't detect enablement changes. We must explicitly track
     * the computed enabled state in component state.
     * 
     * @param {Object} props - Component props
     * @see HJC-661 - Fix for devicelesiongrid enablement tracking
     */
    constructor(props) {
        super(props);
        this.state = {
            wasEnabled: this.isEnabled(props)
        };
        this.clearGridItems = this.clearGridItems.bind(this);
    }

    /**
     * Computes the current enabled state by calling activeRecord.isFieldEnabled().
     * This evaluates the EnableScript and returns the current enabled state.
     * 
     * @param {Object} props - Component props (defaults to this.props)
     * @returns {boolean} True if field is enabled, false otherwise
     */
    isEnabled(props = this.props) {
        const { activeRecord, descriptor } = props;
        return activeRecord.isFieldEnabled(descriptor.key);
    }

    /**
     * Detects enablement state changes and clears grid when transitioning to disabled.
     * This ensures the UI reflects the business logic: when a field becomes disabled,
     * its value should be cleared (empty device list).
     * 
     * @param {Object} prevProps - Previous component props
     */
    componentDidUpdate(prevProps) {
        const currentlyEnabled = this.isEnabled();
        const { wasEnabled } = this.state;
        
        // Detect enablement state change
        if (wasEnabled !== currentlyEnabled) {
            this.setState({ wasEnabled: currentlyEnabled });
            
            // If transitioning from enabled to disabled, clear the grid.
            // Pass prevProps.activeRecord so clearGridItems can read the join records
            // that existed BEFORE the TableStore enablement system cleared them.
            if (!currentlyEnabled && wasEnabled) {
                this.clearGridItems(prevProps.activeRecord);
            }
        }
    }

    /**
     * Clears orphaned pool records when the field transitions from enabled to disabled.
     *
     * The TableStore enablement system automatically clears the join collection
     * (rowSourceEntityCollectionKey) on the activeRecord when a field becomes disabled.
     * By the time clearGridItems is called, activeRecord.get(joinKey) is already [].
     * We therefore read the join records from prevActiveRecord (the pre-disable state)
     * so we know which pool records to clean up.
     *
     * Phase 1 (unlinking from activeRecord's join collection) is already handled by
     * the enablement system. We only need Phase 2: removing orphaned base records
     * from the parent's composition pool (rowSourceEntityFieldKey).
     *
     * @param {EntityProxy} prevActiveRecord - activeRecord from prevProps, holds old join data
     */
    clearGridItems(prevActiveRecord) {
        const { activeRecord, descriptor, fluxContext } = this.props;
        const config = descriptor.config;

        if (!config || !config.rowSourceEntityCollectionKey || !config.columnSourceEntityFieldKey) {
            console.warn("devicelesiongrid: missing config for grid clearing");
            return;
        }

        const parentRecord = activeRecord.get("parent");
        if (!parentRecord) {
            console.warn("devicelesiongrid: parentRecord not found, cannot clear pool");
            return;
        }

        // Read join records from the PREVIOUS state — the current state has already been
        // cleared by the TableStore enablement system, so joinRecords would be [] otherwise.
        const prevRecord = prevActiveRecord || activeRecord;
        const joinRecords = prevRecord.get(config.rowSourceEntityCollectionKey) || [];

        // Other sibling entities in CURRENT state. This weapon's join collection is already
        // cleared, so any other sibling still holding a reference must keep the pool record.
        const allColumnRecords = parentRecord.get(config.columnSourceEntityFieldKey) || [];
        const activeId = String(activeRecord.id);
        const otherColumnRecords = allColumnRecords.filter(r => String(r.id) !== activeId);

        joinRecords.forEach(baseRecord => {
            const baseId = String(baseRecord.id);

            // If any OTHER sibling still references this base record, leave the pool record.
            const referencedElsewhere = otherColumnRecords.some(sibling => {
                const siblingLinks = sibling.get(config.rowSourceEntityCollectionKey) || [];
                return siblingLinks.some(r => String(r.id) === baseId);
            });

            if (!referencedElsewhere) {
                // Not referenced by any other sibling: delete from the parent's pool.
                Dispatcher.contextDispatch(fluxContext, {
                    type: RecordActions.DELETE_CHILD,
                    recordKey: parentRecord.entityKey,
                    recordId: parentRecord.id,
                    fieldKey: config.rowSourceEntityFieldKey,
                    childKey: baseRecord.entityKey,
                    childId: baseRecord.id,
                    [OPT_USER_CONFIRMED]: true,
                });
            }
            // If referenced elsewhere, Phase 1 is already handled by the enablement system;
            // the pool record stays for the other siblings that still reference it.
        });
    }

    render() {
        const {
            controlOnly,
            descriptor,
            fluxContext,
            activeRecord,
            onFocus,
            onBlur,
            onKeyDown
        } = this.props;
        const config = descriptor.config;
        const parentRecord = activeRecord.get("parent");

        const creatables = [];
        for (let i = 0, l = config.creatables.length; i < l; i++) {
            const creatable = config.creatables[i];
            const ei = entityMetaStore.getInfo(creatable.key);
            creatables.push(buildCreatable(
                ei.implKey,
                creatable.name,
                creatable.key,
                creatable.collectionKey,
                parentRecord,
            ));
        }

        const columnCollection = parentRecord.get(config.columnSourceEntityFieldKey);
        const columns = buildColumnsFromConfig(config.columns, this.props)
            .concat(
                buildDevicelesiongridCheckboxColumns(
                    this.props, columnCollection,
                ),
            );

        return (
            <RecordGridDataGateway
                key={descriptor.key}
                enabled={this.isEnabled()}
                component={Control}
                controlOnly={controlOnly}
                colCoords={{
                    record: parentRecord,
                    fieldKey: config.rowSourceEntityFieldKey,
                }}
                activeRecord={activeRecord}
                shortname={config.rowSourceEntityFieldKey}
                singularLabel={config.otherEntityName}
                fluxContext={fluxContext}
                descriptor={descriptor}
                addMode={config.addMode}
                allowView={!config.inline}
                allowCreate={true}
                allowRemove={true}
                columns={columns}
                creatables={creatables}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
            />
        );
    }

}

AssociationFielddevicelesiongrid.propTypes = {
    descriptor: PropTypes.shape(descriptorPropTypes({
        inline: PropTypes.bool,
        otherEntityName: PropTypes.string.isRequired,
        rowSourceEntityFieldKey: PropTypes.string.isRequired,
        columnSourceEntityFieldKey: PropTypes.string.isRequired,
        rowSourceEntityCollectionKey: PropTypes.string.isRequired,
        addMode: PropTypes.string.isRequired,
        columns: PropTypes.arrayOf(PropTypes.shape({
            heading: PropTypes.string.isRequired,
            width: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]).isRequired,
            type: PropTypes.string.isRequired,
            script: PropTypes.string,
            itemInfo: PropTypes.object,
        })).isRequired,
        creatables: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string.isRequired,
            key: PropTypes.string.isRequired,
            collectionKey: PropTypes.string.isRequired,
        })).isRequired,
    })),
    fluxContext: PropTypes.string.isRequired,
    activeRecord: PropTypes.instanceOf(EntityProxy).isRequired,
    controlOnly: PropTypes.bool,
    onFocus: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func.isRequired,
};

export default AssociationFielddevicelesiongrid;



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/dataentryform/formitems/AssociationField/devicelesiongrid.js